import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiPlus } from "react-icons/bi";
import { MdDownload, MdUploadFile } from "react-icons/md";
import {
  addEdge,
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import cuid from "cuid";
import Header from "shared/components/Header";
import { NAME_SPACES } from "shared/locales/constants";
import { EDGE_KINDS, NODE_TYPES } from "utils/constants";
import ConditionsPanel from "./ConditionsPanel";
import ImportSummaryModal from "./ImportSummaryModal";
import StatusNode from "./StatusNode";
import StatusPanel from "./StatusPanel";
import { applyLayout } from "./layout";
import { edgeSummary, fromGraph, toGraph } from "./graph";
import {
  attachGraphScreens,
  attachStatusScreens,
  processToConfig,
  serializeConfig,
} from "./importConfig";

const nodeTypes = { status: StatusNode };

const defaultEdgeOptions = {
  type: "smoothstep",
  markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18 },
  style: { strokeWidth: 1.5 },
};

const fitViewOptions = { padding: 0.2 };

const View = ({
  process = {},
  variables = [],
  cancel,
  onSave,
  saving,
  reload,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const CREATE = t("CREATE", { returnObjects: true });
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [requireEmbeddedScreens, setRequireEmbeddedScreens] = useState(false);

  const onImport = ({ target }) => {
    const file = target.files && target.files[0];
    if (file) setPendingFile(file);
    target.value = "";
  };

  const openImport = (withScreens = false) => {
    setRequireEmbeddedScreens(withScreens);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const closeImport = () => {
    setPendingFile(null);
    if (reload) reload();
  };

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  useEffect(() => {
    const graph = toGraph(process.processStatuses || []);
    const hasSavedPositions = graph.nodes.some(
      (node) => node.position && (node.position.x || node.position.y),
    );
    setNodes(
      hasSavedPositions ? graph.nodes : applyLayout(graph.nodes, graph.edges),
    );
    setEdges(graph.edges);
    setSelectedEdgeId(null);
    setSelectedNodeId((current) =>
      graph.nodes.some((node) => node.id === current) ? current : null,
    );
  }, [process, setNodes, setEdges]);

  const onConnect = useCallback(
    (connection) => {
      if (connection.source === connection.target) return;
      setEdges((current) =>
        addEdge(
          {
            ...connection,
            id: cuid(),
            label: "",
            data: {
              possibleStatusId: cuid(),
              order: 1,
              kind: EDGE_KINDS.CONDITION,
              event: "",
              delaySeconds: null,
              conditions: [],
            },
          },
          current,
        ),
      );
    },
    [setEdges],
  );

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
  }, []);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  }, []);

  const updateSelectedNode = (field, value) =>
    setNodes((current) =>
      current.map((node) =>
        node.id === selectedNodeId
          ? { ...node, data: { ...node.data, [field]: value } }
          : node,
      ),
    );

  const addStatus = () => {
    const id = cuid();
    const maxOrder = nodes.reduce(
      (max, node) => Math.max(max, parseInt(node.data.order, 10) || 0),
      0,
    );
    setNodes((current) => [
      ...current,
      {
        id,
        type: "status",
        position: { x: 80 + current.length * 24, y: 80 },
        data: {
          name: "",
          tag: undefined,
          color: "",
          background: "",
          progress: "",
          order: String(maxOrder + 1),
          template: "",
          moveToNext: false,
          nodeType: NODE_TYPES.STATUS,
          config: {},
          actions: [],
          formDefinition: null,
          entryConnectors: [],
          outputConnectors: [],
          dataBindings: [],
          screenCount: 0,
          screenId: null,
          screenVersionId: null,
          screenVersionStatus: null,
          screenHtml: null,
          screenReport: null,
        },
      },
    ]);
    setSelectedEdgeId(null);
    setSelectedNodeId(id);
  };

  const deleteSelectedNode = () => {
    setNodes((current) => current.filter((node) => node.id !== selectedNodeId));
    setEdges((current) =>
      current.filter(
        (edge) =>
          edge.source !== selectedNodeId && edge.target !== selectedNodeId,
      ),
    );
    setSelectedNodeId(null);
  };

  const setSelectedConditions = (conditions) =>
    setEdges((current) =>
      current.map((edge) =>
        edge.id === selectedEdgeId
          ? {
              ...edge,
              label: edgeSummary({ ...edge.data, conditions }),
              data: { ...edge.data, conditions },
            }
          : edge,
      ),
    );

  const updateSelectedEdgeMeta = (field, value) =>
    setEdges((current) =>
      current.map((edge) => {
        if (edge.id !== selectedEdgeId) return edge;
        const data = { ...edge.data, [field]: value };
        return { ...edge, label: edgeSummary(data), data };
      }),
    );

  const deleteSelectedEdge = () => {
    setEdges((current) => current.filter((edge) => edge.id !== selectedEdgeId));
    setSelectedEdgeId(null);
  };

  const handleSave = () =>
    onSave(fromGraph(nodes, edges, process.processStatuses || []));

  const downloadConfig = (config, suffix = "") => {
    const text = serializeConfig(config, "yaml");
    const slug = (process.name || "canvas")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const blob = new Blob([text], { type: "text/yaml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slug || "canvas"}${suffix}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = (includeScreens = false) => {
    const graphStatuses = fromGraph(
      nodes,
      edges,
      process.processStatuses || [],
    );
    const processStatuses = includeScreens
      ? attachGraphScreens(
          attachStatusScreens(graphStatuses, process.processStatuses),
          nodes,
        )
      : graphStatuses;
    const config = processToConfig(
      { ...process, processStatuses },
      { includeScreens },
    );
    downloadConfig(config, includeScreens ? "-complet" : "");
  };

  const selectedEdge = edges.find((edge) => edge.id === selectedEdgeId) || null;
  const selectedNode = nodes.find((node) => node.id === selectedNodeId) || null;
  const selectedNodePersisted =
    selectedNode !== null &&
    (process.processStatuses || []).some(
      (status) => status.id === selectedNode.id,
    );
  const nodeName = (id) => {
    const node = nodes.find((item) => item.id === id);
    return node ? node.data.name : null;
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".yaml,.yml,.json"
        className="canvas-editor__hidden"
        onChange={onImport}
      />
      <ImportSummaryModal
        file={pendingFile}
        existingProcess={process}
        requireEmbeddedScreens={requireEmbeddedScreens}
        onClose={closeImport}
      />
      <Header
        title={FORM.CANVAS || "Canvas"}
        subtitle={process.name}
        actions={[
          {
            text: CREATE.HEADER.DISCARD,
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: FORM.IMPORT || "Importer",
            onClick: () => openImport(false),
            type: "primary--outlined",
            icon: <MdUploadFile size={20} />,
          },
          {
            text: FORM.EXPORT || "Exporter",
            onClick: () => handleExport(false),
            type: "primary--outlined",
            icon: <MdDownload size={20} />,
          },
          {
            text: FORM.IMPORT_ALL || "Importer tout",
            onClick: () => openImport(true),
            type: "primary--outlined",
            icon: <MdUploadFile size={20} />,
          },
          {
            text: FORM.EXPORT_ALL || "Exporter tout",
            onClick: () => handleExport(true),
            type: "primary--outlined",
            icon: <MdDownload size={20} />,
          },
          {
            text: FORM.ADD_STATUS || "Ajouter un statut",
            onClick: addStatus,
            type: "secondary",
            icon: <BiPlus size={20} />,
          },
          {
            text: CREATE.HEADER.SAVE,
            onClick: handleSave,
            type: "primary",
            icon: <BiCheck size={20} />,
            disabled: saving,
          },
        ]}
      />
      <div className="canvas-editor__viewport">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          fitViewOptions={fitViewOptions}
          minZoom={0.2}
          nodesDeletable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          <Controls showInteractive={false} />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>
      <ConditionsPanel
        edge={selectedEdge}
        variables={variables}
        sourceName={selectedEdge ? nodeName(selectedEdge.source) : null}
        targetName={selectedEdge ? nodeName(selectedEdge.target) : null}
        onChange={setSelectedConditions}
        onMetaChange={updateSelectedEdgeMeta}
        onDelete={deleteSelectedEdge}
        onClose={() => setSelectedEdgeId(null)}
      />
      <StatusPanel
        node={selectedNode}
        nodes={nodes}
        processId={process.id}
        persisted={selectedNodePersisted}
        variables={variables}
        onChange={updateSelectedNode}
        onDelete={deleteSelectedNode}
        onClose={() => setSelectedNodeId(null)}
        reload={reload}
      />
    </>
  );
};

export default View;
